import { Box, Paper, useTheme } from "@mui/material";
import * as React from "react";

import { SidebarLayoutContext } from "./SidebarLayoutContext";

interface MainLayoutWithSidebarProps extends React.PropsWithChildren {
  sidebarContent: React.ReactElement | null;
  sidebarWidth?: string;
  sidebarControlOverrides?: {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
  };
}

export const MainLayoutWithSidebar: React.FC<MainLayoutWithSidebarProps> = ({
  children,
  sidebarContent,
  sidebarControlOverrides,
  sidebarWidth = "244px",
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [shouldAnimate, setShouldAnimate] = React.useState<boolean>(false);

  const canonicalIsOpen =
    sidebarControlOverrides === undefined
      ? isOpen
      : sidebarControlOverrides.isOpen;
  const canonicalSetIsOpen =
    sidebarControlOverrides === undefined
      ? setIsOpen
      : sidebarControlOverrides.setIsOpen;

  const toggleOpen = () => {
    canonicalSetIsOpen(!canonicalIsOpen);
  };

  const theme = useTheme();

  // Turn the animation on AFTER the display is set to block
  React.useEffect(() => {
    if (canonicalIsOpen) {
      setShouldAnimate(true);
    } else {
      setShouldAnimate(false);
    }
  }, [canonicalIsOpen]);

  return (
    <SidebarLayoutContext.Provider
      value={{
        isOpen: canonicalIsOpen,
        toggleOpen: toggleOpen,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifySelf: "stretch",
          position: "relative",
          overflow: "hidden",
          height: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            maxWidth: {
              xs: canonicalIsOpen
                ? `calc(100% - ${sidebarWidth} - 20px)`
                : `calc(100%)`,
            },
            transition: "max-width 0.3s ease-in-out",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </Box>
        <Box
          component={Paper}
          elevation={0}
          sx={{
            zIndex: theme.zLayer(3),
            width: sidebarWidth,
            maxWidth: sidebarWidth,
            overflowX: "hidden",
            overflowY: "auto",
            transform: shouldAnimate ? "translateX(0)" : "translateX(100%)",
            transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            backgroundColor: theme.palette.foreground.main,
            p: theme.spacing(2),
            border: theme.border.thin,
          }}
        >
          {sidebarContent}
        </Box>
      </Box>
    </SidebarLayoutContext.Provider>
  );
};
